import { Table, Typography } from 'antd';
import { useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import type { ModelPaymentAccount } from '@app/api/PaymentAccounts';
import type { NClientPayments } from '@app/types/ClientPayments';
import type { NLegalEntities } from '@app/types/LegalEntities';
import {
  addLeadingZeroes,
  divideAmount,
  formatISODateToCustomFormat,
} from '@app/utils';

import {
  COLUMNS,
  FO_CLIENT,
  FO_DETAILS,
  FO_NUMBER,
  FO_OPERATION,
  FO_RECORD,
  FO_SERVICE_OR_PRODUCT,
  FO_SUM,
  FO_USER,
} from '@pages/Finances/FinancialOperations/constants/columns';
import {
  FO_PAY_BANK,
  FO_PAY_CASH,
} from '@pages/Finances/FinancialOperations/constants/payTypes';

import type { NFinancialOperations } from '@pages/Finances/FinancialOperations/FinancialOperations.types';

const { Text } = Typography;

const handleSecondaryDetailsCell = (
  PaymentAccount: ModelPaymentAccount['Name'],
  LegalEntity: NLegalEntities.ModelLegalEntity['Name']
) => {
  if (PaymentAccount && LegalEntity) {
    return `${PaymentAccount}, ${LegalEntity}`;
  } else if (PaymentAccount && !LegalEntity) {
    return `${PaymentAccount}`;
  } else if (!PaymentAccount && LegalEntity) {
    return `${LegalEntity}`;
  } else if (!PaymentAccount && !LegalEntity) {
    return '-';
  }
};

const handlePayType = (payType: NClientPayments.PayType) => {
  if (payType === FO_PAY_CASH) {
    return 'Оплата наличными';
  } else if (payType === FO_PAY_BANK) {
    return 'Оплата картой';
  } else {
    return '-';
  }
};

export const FinancialOperationsTable: NFinancialOperations.FinancialOperationsTable =
  ({
    tableLoader,
    operationsList,
    rowsPerPage,
    isMobile,
    handleTableChange,
    pageNumber,
    totalRowsCount,
  }) => {
    const [, setSearchParams] = useSearchParams();
    const onOpenRecord = useCallback(
      (id: number) => {
        setSearchParams((prev) => {
          const params = new URLSearchParams(prev);
          params.set('recordId', id.toString());
          return params;
        });
      },
      [setSearchParams]
    );

    const data: NFinancialOperations.Columns[] = (operationsList || []).map(
      ({
        Id,
        UserId,
        RecordId,
        ClientService,
        Amount,
        ClientId,
        ClientName,
        UserName,
        PayType,
        PaymentAccount,
        Timestamp,
        LegalEntity,
      }) => ({
        key: Id,
        [FO_NUMBER]: Id,
        [FO_OPERATION]: (
          <>
            <Text>{Amount >= 0 ? 'Оплата' : 'Возврат'}</Text>
            <br />
            <Text type="secondary">
              {formatISODateToCustomFormat(Timestamp)}
            </Text>
          </>
        ),
        [FO_RECORD]: (
          <Text
            style={{ color: '#1677ff', cursor: 'pointer' }}
            onClick={() => {
              onOpenRecord(RecordId);
            }}
          >
            {String(addLeadingZeroes(String(RecordId)))}
          </Text>
        ),
        [FO_SERVICE_OR_PRODUCT]: ClientService || '',
        [FO_SUM]: (
          <Text type={Amount >= 0 ? undefined : 'danger'}>
            {divideAmount(Amount)}
          </Text>
        ),
        [FO_DETAILS]: (
          <>
            <Text>{handlePayType(PayType)}</Text>
            <br />
            {PaymentAccount !== null && LegalEntity !== null && (
              <Text type="secondary">
                {handleSecondaryDetailsCell(PaymentAccount, LegalEntity)}
              </Text>
            )}
          </>
        ),
        [FO_CLIENT]: (
          <Link to={`/clients/card/?id=${ClientId}`}>{ClientName || ''}</Link>
        ),
        [FO_USER]: (
          <Link to={`/users/edit/?id=${UserId}&key=1`}>{UserName || ''}</Link>
        ),
      })
    );

    return (
      <Table
        loading={tableLoader}
        columns={COLUMNS}
        dataSource={data}
        scroll={{
          x: 1300,
        }}
        pagination={{
          size: isMobile ? 'small' : 'default',
          showSizeChanger: true,
          pageSize: rowsPerPage,
          position: isMobile ? ['bottomCenter'] : ['bottomLeft'],
          showTotal: (total, range) =>
            `Показаны ${range[0]}-${range[1]} из ${total}`,
          onChange: handleTableChange,
          current: pageNumber,
          total: totalRowsCount,
        }}
      />
    );
  };
